import React, { useMemo, useState, useCallback, useRef } from 'react';
import { Clock, AlertTriangle, MessageSquare, ChevronRight, Layers, ArrowRight, Loader } from 'lucide-react';
import { PROJECT_STATUSES } from '../../constants/appConstants';

// -----------------------------------------------------------------------
// Diálogo de confirmação de mudança de status via Kanban
// -----------------------------------------------------------------------
const KanbanMoveConfirmDialog = ({ isOpen, onClose, onConfirm, project, targetStatus, isProcessing }) => {
    if (!isOpen || !project || !targetStatus) return null;

    const targetStatusDef = PROJECT_STATUSES.find(s => s.value === targetStatus);
    const fromStatusDef = PROJECT_STATUSES.find(s => s.value === project.status);

    return (
        <div
            className="fixed inset-0 bg-black/60 z-[1100] flex justify-center items-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="kanban-confirm-title"
        >
            <div
                className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md p-6 flex flex-col gap-4"
                onClick={e => e.stopPropagation()}
            >
                {/* Cabeçalho */}
                <div>
                    <h3 id="kanban-confirm-title" className="text-lg font-bold text-slate-800 dark:text-slate-100">
                        Confirmar Mudança de Status
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                        Você está movendo o projeto <strong className="text-slate-700 dark:text-slate-200">{project.protocolo}</strong>.
                    </p>
                </div>

                {/* Seta de status */}
                <div className="flex items-center gap-3 bg-slate-50 dark:bg-slate-800 rounded-xl px-4 py-3">
                    <span
                        className="text-sm font-semibold px-3 py-1 rounded-full"
                        style={{ background: `${fromStatusDef?.color}25`, color: fromStatusDef?.color }}
                    >
                        {fromStatusDef?.label || project.status}
                    </span>
                    <ArrowRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
                    <span
                        className="text-sm font-bold px-3 py-1 rounded-full"
                        style={{ background: `${targetStatusDef?.color}25`, color: targetStatusDef?.color }}
                    >
                        {targetStatusDef?.label || targetStatus}
                    </span>
                </div>

                <p className="text-sm text-slate-600 dark:text-slate-300">
                    O status será atualizado para <strong>{targetStatusDef?.label || targetStatus}</strong> com a data de hoje. Esta alteração será registrada no histórico do projeto.
                </p>

                {/* Botões */}
                <div className="flex justify-end gap-3 pt-2">
                    <button
                        onClick={onClose}
                        disabled={isProcessing}
                        className="btn-secondary disabled:opacity-50"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={isProcessing}
                        className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isProcessing
                            ? <><Loader className="animate-spin h-4 w-4" /> Salvando...</>
                            : 'Confirmar'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// -----------------------------------------------------------------------
// Componente de mini-card para o modo Kanban
// -----------------------------------------------------------------------
const KanbanCard = ({ project, onCardClick, onDragStart, onDragEnd, isDragging }) => {
    const statusInfo = PROJECT_STATUSES.find(
        s => s.value.toLowerCase() === (project.status || '').toLowerCase()
    ) || { label: project.status || 'Desconhecido', tagClasses: '' };

    /**
     * Calcula a tag de urgência de prazo do projeto.
     * @returns {{ text: string, icon: React.ReactNode, className: string } | null}
     */
    const getStatusTag = () => {
        if (project.status === 'Aprovado' || !project.dueDate) return null;

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const dueDate = new Date(project.dueDate + 'T00:00:00-03:00');
        const diffDays = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
            return {
                text: 'Atrasado',
                icon: <Clock className="h-3 w-3 mr-1" />,
                className: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
            };
        } else if (diffDays <= 15) {
            return {
                text: 'Atenção!',
                icon: <AlertTriangle className="h-3 w-3 mr-1" />,
                className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300',
            };
        }
        return null;
    };

    const urgencyTag = getStatusTag();

    return (
        <div
            draggable
            onDragStart={e => onDragStart(e, project)}
            onDragEnd={onDragEnd}
            onClick={() => onCardClick(project)}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onCardClick(project);
                }
            }}
            aria-label={`Projeto ${project.protocolo}, ${project.cidade}. Status: ${statusInfo.label}. Arraste para mover entre colunas.`}
            className={`
                group bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700
                p-3 cursor-grab active:cursor-grabbing
                hover:border-blue-500 dark:hover:border-blue-400
                hover:shadow-md dark:hover:shadow-slate-900/60
                transition-all duration-150
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 dark:focus:ring-offset-slate-900
                select-none
                ${isDragging ? 'opacity-40 scale-95 rotate-1 shadow-lg' : ''}
            `}
        >
            {/* Protocolo */}
            <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/40 px-2 py-0.5 rounded">
                    {project.protocolo}
                </span>
                <ChevronRight className="h-4 w-4 text-slate-400 dark:text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>

            {/* Cidade / Local */}
            <p className="text-sm font-medium text-slate-800 dark:text-slate-100 leading-tight">
                {project.cidade}
            </p>
            {project.local && (
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">
                    {project.local}
                </p>
            )}

            {/* Postes */}
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                {project.totalPostes} postes
            </p>

            {/* Tags de urgência e comentários */}
            {(urgencyTag || (project.comments && project.comments.length > 0)) && (
                <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                    {urgencyTag && (
                        <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full flex items-center ${urgencyTag.className}`}>
                            {urgencyTag.icon}{urgencyTag.text}
                        </span>
                    )}
                    {project.comments && project.comments.length > 0 && (
                        <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full flex items-center bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300">
                            <MessageSquare className="h-3 w-3 mr-1" />
                            {project.comments.length}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

// -----------------------------------------------------------------------
// Componente principal do quadro Kanban
// -----------------------------------------------------------------------

/**
 * @description Exibe a lista de projetos agrupada por status em colunas Kanban,
 *              com suporte a arrastar e soltar entre colunas.
 * @param {object} props
 * @param {Array}    props.projects           - Lista de projetos (já filtrada/ordenada pela página pai).
 * @param {function} props.onCardClick        - Função de callback ao clicar em um card.
 * @param {function} props.onUpdateStatus     - Função chamada ao confirmar a mudança de status via drag-and-drop.
 *                                              Assinatura: (projectId, newStatus, newStatusDate) => Promise<void>
 */
const KanbanBoard = ({ projects, onCardClick, onUpdateStatus }) => {
    // --- Estado de drag-and-drop ---
    const draggedProjectRef = useRef(null);
    const [draggingId, setDraggingId] = useState(null);
    const [overColumn, setOverColumn] = useState(null);

    // --- Estado do diálogo de confirmação ---
    const [confirmState, setConfirmState] = useState({
        isOpen: false,
        project: null,
        targetStatus: null,
        isProcessing: false,
    });

    // Agrupa os projetos por status, preservando a ordem definida em PROJECT_STATUSES
    const columns = useMemo(() => {
        const groups = {};
        PROJECT_STATUSES.forEach(s => {
            groups[s.value] = [];
        });

        projects.forEach(project => {
            const key = project.status || '';
            if (groups[key] !== undefined) {
                groups[key].push(project);
            } else {
                // Status desconhecido — cria grupo ad-hoc
                groups[key] = groups[key] || [];
                groups[key].push(project);
            }
        });

        return PROJECT_STATUSES.map(statusDef => ({
            ...statusDef,
            projects: groups[statusDef.value] || [],
        }));
    }, [projects]);

    // --- Handlers de Drag ---
    const handleDragStart = useCallback((e, project) => {
        draggedProjectRef.current = project;
        setDraggingId(project.id);
        // Necessário para Firefox
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', project.id);
    }, []);

    const handleDragEnd = useCallback(() => {
        setDraggingId(null);
        setOverColumn(null);
        draggedProjectRef.current = null;
    }, []);

    const handleDragOver = useCallback((e, columnStatus) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setOverColumn(columnStatus);
    }, []);

    const handleDragLeave = useCallback(() => {
        setOverColumn(null);
    }, []);

    const handleDrop = useCallback((e, columnStatus) => {
        e.preventDefault();
        setOverColumn(null);

        const project = draggedProjectRef.current;
        if (!project) return;

        // Não faz nada se soltar na mesma coluna
        if (project.status === columnStatus) {
            setDraggingId(null);
            draggedProjectRef.current = null;
            return;
        }

        // Abre o diálogo de confirmação
        setConfirmState({
            isOpen: true,
            project,
            targetStatus: columnStatus,
            isProcessing: false,
        });

        setDraggingId(null);
        draggedProjectRef.current = null;
    }, []);

    const handleConfirmMove = useCallback(async () => {
        const { project, targetStatus } = confirmState;
        if (!project || !targetStatus || !onUpdateStatus) return;

        setConfirmState(prev => ({ ...prev, isProcessing: true }));

        // Data atual formatada como YYYY-MM-DD (fuso de Brasília)
        const today = new Date();
        const statusDate = today.toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' }); // 'en-CA' = YYYY-MM-DD

        try {
            await onUpdateStatus(project.id, targetStatus, statusDate);
        } finally {
            setConfirmState({ isOpen: false, project: null, targetStatus: null, isProcessing: false });
        }
    }, [confirmState, onUpdateStatus]);

    const handleCloseConfirm = useCallback(() => {
        if (confirmState.isProcessing) return;
        setConfirmState({ isOpen: false, project: null, targetStatus: null, isProcessing: false });
    }, [confirmState.isProcessing]);

    if (projects.length === 0) {
        return null; // A página pai renderiza o estado vazio
    }

    return (
        <>
            {/* Diálogo de confirmação de mudança de status */}
            <KanbanMoveConfirmDialog
                isOpen={confirmState.isOpen}
                onClose={handleCloseConfirm}
                onConfirm={handleConfirmMove}
                project={confirmState.project}
                targetStatus={confirmState.targetStatus}
                isProcessing={confirmState.isProcessing}
            />

            {/* Quadro Kanban — rolagem horizontal no contêiner */}
            <div
                className="flex gap-4 overflow-x-auto pb-4"
                style={{ minHeight: '400px' }}
                aria-label="Quadro Kanban de projetos"
            >
                {columns.map(col => (
                    <KanbanColumn
                        key={col.value}
                        statusDef={col}
                        projects={col.projects}
                        onCardClick={onCardClick}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        draggingId={draggingId}
                        isOver={overColumn === col.value}
                    />
                ))}
            </div>
        </>
    );
};

// -----------------------------------------------------------------------
// Coluna individual do Kanban
// -----------------------------------------------------------------------
const KanbanColumn = ({
    statusDef, projects, onCardClick,
    onDragStart, onDragEnd, onDragOver, onDragLeave, onDrop,
    draggingId, isOver,
}) => {
    const isEmpty = projects.length === 0;

    return (
        <div
            className="flex flex-col flex-shrink-0 w-72"
            aria-label={`Coluna ${statusDef.label}, ${projects.length} projetos`}
        >
            {/* Cabeçalho da coluna */}
            <div
                className="flex items-center justify-between px-3 py-2 rounded-t-lg mb-2"
                style={{ borderTop: `3px solid ${statusDef.color}`, background: `${statusDef.color}18` }}
            >
                <span className="text-sm font-bold text-slate-700 dark:text-slate-200 truncate">
                    {statusDef.label}
                </span>
                <span
                    className="text-xs font-bold px-2 py-0.5 rounded-full ml-2 flex-shrink-0"
                    style={{ background: `${statusDef.color}30`, color: statusDef.color }}
                >
                    {projects.length}
                </span>
            </div>

            {/* Zona de drop — lista de cards com scroll interno */}
            <div
                onDragOver={e => onDragOver(e, statusDef.value)}
                onDragLeave={onDragLeave}
                onDrop={e => onDrop(e, statusDef.value)}
                className={`
                    flex flex-col gap-2 overflow-y-auto max-h-[calc(100vh-340px)] pr-1
                    rounded-lg transition-colors duration-150 min-h-[80px] p-1
                    ${isOver
                        ? 'bg-blue-50 dark:bg-blue-900/20 ring-2 ring-inset ring-blue-400 dark:ring-blue-500'
                        : 'bg-transparent'
                    }
                `}
                aria-dropeffect="move"
            >
                {isEmpty && !isOver ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center rounded-lg border-2 border-dashed border-slate-200 dark:border-slate-700">
                        <Layers className="h-6 w-6 text-slate-300 dark:text-slate-600 mb-1" />
                        <p className="text-xs text-slate-400 dark:text-slate-500">Nenhum projeto</p>
                    </div>
                ) : (
                    projects.map(project => (
                        <KanbanCard
                            key={project.id}
                            project={project}
                            onCardClick={onCardClick}
                            onDragStart={onDragStart}
                            onDragEnd={onDragEnd}
                            isDragging={draggingId === project.id}
                        />
                    ))
                )}

                {/* Indicador visual de "solte aqui" quando a coluna está sendo alvo e está vazia */}
                {isOver && isEmpty && (
                    <div className="flex flex-col items-center justify-center py-6 rounded-lg border-2 border-dashed border-blue-400 dark:border-blue-500 text-blue-500 dark:text-blue-400 text-xs font-medium">
                        Solte aqui
                    </div>
                )}
            </div>
        </div>
    );
};

export default KanbanBoard;
